import { Component, Input, OnInit } from '@angular/core';
import { ReportsService } from '../../../shared/services/reports.service';
import { catchError, EMPTY, mergeMap, switchMap, tap } from 'rxjs';
import { ApiError } from '../../../shared/models/error';
import {
    AssignmentBillResult,
    LoadDetail,
    AssignmentReport,
    Type,
    Task,
} from '../../../shared/types/reports';
import { AuthService } from '../../../shared/services/auth.service';
import { User } from '../../../shared/types/users/User';
import { UserSessionService } from '../../../shared/services/user-session.service';
import { fromIterable } from 'rxjs/internal/observable/innerFrom';
import { map, takeUntil } from 'rxjs/operators';
import { DestroyRxComponent } from '../../../components/destroy-rx/destroy-rx.component';
import _ from 'lodash';

@Component({
    selector: 'las-user-assignments',
    templateUrl: './user-assignments.component.html',
    styleUrls: ['./user-assignments.component.scss'],
    standalone: false,
})
export class UserAssignmentsComponent
    extends DestroyRxComponent
    implements OnInit
{
    protected readonly Type = Type;

    @Input() user!: User;
    @Input() hideTitle = false;

    activeState!: AssignmentState;
    pendingState!: AssignmentState;
    canceledState!: AssignmentState;
    completedState!: AssignmentState;
    coordinatedState!: AssignmentState;

    selectedIndex = 0;

    // State backing the currently selected tab, so one report/filter serves all tabs.
    get selectedState(): AssignmentState {
        switch (this.selectedIndex) {
            case 1:
                return this.pendingState;
            case 2:
                return this.completedState;
            case 3:
                return this.canceledState;
            case 4:
                return this.coordinatedState;
            default:
                return this.activeState;
        }
    }

    /**
     * Sets the default state for all assignment types.
     * @param reports The service for getting  a single assignment type.
     * @param auth Service to get the las-user if possible.
     * @param userSession Service providing the session$ observable.
     */
    constructor(
        private reports: ReportsService,
        private auth: AuthService,
        private userSession: UserSessionService,
    ) {
        super();
        this.resetAllStates();
    }

    /**
     * Constructs the pipeline for loading all assignment types when the user session is changed from the navbar.
     * The user is also populated from the auth service if it wasn't passed in as an input.
     */
    ngOnInit(): void {
        if (!this.user) this.user = this.auth.user;
        this.userSession.session$
            .pipe(
                tap((_) => this.resetAllStates()),
                // NOTE: We use switchMap to cover the case where a user quickly selects sessions one after another
                //  creating a race condition. This way, the previous http call is canceled.
                switchMap((session) =>
                    fromIterable(this.typeSessionPairs(session.name)),
                ),
                // Map to the load detail, so we can later map to a tuple with the assignment results below.
                map((typeSessionPair) =>
                    this.getRequestDetails(typeSessionPair),
                ),
                // Merge one load detail to it's corresponding assignments observable.
                mergeMap((detail) =>
                    this.getAssignmentsForType(detail).pipe(
                        // We need to map here because we need the detail in `subscribe` to use the `set` property.
                        map((assignments) => [detail, assignments] as const),
                    ),
                ),
                catchError((error) => this.handleError(error)),
                takeUntil(this.destroy$),
            )
            .subscribe((detailAssignments) => {
                let [detail, assignments] = detailAssignments;
                assignments = assignments.filter(detail.filter);
                const filteredGroups = this.groupRequestsTogether(assignments);
                detail.set(filteredGroups);
            });
    }

    /**
     * Returns an AssignmentState object in its default state.
     */
    get baseState(): AssignmentState {
        return {
            isLoading: true,
            assignments: [],
        };
    }

    /**
     * Returns a typed state for use in the view's ng-template.
     * @param state An assignment state from the view.
     */
    asState = (state: AssignmentState) => state;

    /**
     * Generator (think IEnumerable) for making session/type tuples.
     * @param session The user's current selected session.
     * @private
     */
    private *typeSessionPairs(session: string): Generator<[string, Type]> {
        yield [session, Type.Active];
        yield [session, Type.Cancelled];
        yield [session, Type.Completed];
        yield [session, Type.Coordinators];
        yield [session, Type.Pending];
    }

    private resetAllStates() {
        this.activeState = this.baseState;
        this.canceledState = this.baseState;
        this.completedState = this.baseState;
        this.coordinatedState = this.baseState;
        this.pendingState = this.baseState;
    }

    /**
     * Builds the load detail objects that contain everything needed to populate the assignments for a given type.
     *
     * @see LoadDetail
     *
     * @param sessionTypePair A tuple for determining the type to build and populate the session.
     * @private
     */
    private getRequestDetails(sessionTypePair: [string, Type]) {
        const [session, type] = sessionTypePair;
        const assignmentReport = this.initUserAssignmentReport(session);
        const noFilter = (_: any) => true;
        let detail: LoadDetail;
        switch (type) {
            case Type.Active:
                detail = {
                    report: assignmentReport,
                    requestStatus: ['Analysis Required'],
                    assignmentStatus: [Type.Active],
                    assignmentTask: [Task.Analyze, Task.Approve],
                    get: () => this.activeState.assignments,
                    set: (results: AssignmentBillResult[]) =>
                        this.setState(results, this.activeState),
                    filter: (assignment) => {
                        const list = assignment.assignmentList[0];
                        return !(
                            list.assignmentOrder !==
                                list.currentAssignmentOrder ||
                            (list.assignmentsCount &&
                                list.assignmentsCount > 0 &&
                                list.assignmentTask === 'Coordinator')
                        );
                    },
                };
                break;
            case Type.Pending:
                detail = {
                    report: assignmentReport,
                    requestStatus: ['Analysis Required'],
                    assignmentStatus: [Type.Pending],
                    assignmentTask: [Task.Analyze, Task.Approve],
                    get: () => this.pendingState.assignments,
                    set: (results: AssignmentBillResult[]) =>
                        this.setState(results, this.pendingState),
                    filter: (assignment) => {
                        const list = assignment.assignmentList[0];
                        return !(
                            (list.assignmentOrder &&
                                list.currentAssignmentOrder &&
                                list.assignmentOrder <=
                                    list.currentAssignmentOrder) ||
                            list.assignmentTask === 'Coordinator'
                        );
                    },
                };
                break;
            case Type.Completed:
                detail = {
                    report: assignmentReport,
                    requestStatus: ['Analysis Required', 'Complete'],
                    assignmentStatus: [Type.Completed],
                    assignmentTask: [],
                    get: () => this.completedState.assignments,
                    set: (results: AssignmentBillResult[]) =>
                        this.setState(results, this.completedState),
                    filter: (assignment) => {
                        const {
                            assignmentOrder,
                            assignmentTask,
                            currentAssignmentOrder,
                            requestStatus,
                        } = assignment.assignmentList[0];
                        return !(
                            assignmentOrder &&
                            currentAssignmentOrder &&
                            assignmentOrder >= currentAssignmentOrder &&
                            requestStatus !== 'Complete' &&
                            assignmentTask !== 'Coordinator'
                        );
                    },
                };
                break;
            case Type.Cancelled:
                detail = {
                    report: assignmentReport,
                    requestStatus: [Type.Cancelled],
                    assignmentStatus: [Type.Cancelled],
                    assignmentTask: [],
                    get: () => this.canceledState.assignments,
                    set: (results: AssignmentBillResult[]) =>
                        this.setState(results, this.canceledState),
                    filter: noFilter,
                };
                break;
            case Type.Coordinators:
                detail = {
                    report: assignmentReport,
                    requestStatus: ['Analysis Required'],
                    assignmentStatus: [],
                    assignmentTask: [Task.Coordinate],
                    get: () => this.coordinatedState.assignments,
                    set: (results: AssignmentBillResult[]) =>
                        this.setState(results, this.coordinatedState),
                    filter: (assignment) => {
                        const { assignmentTask, requestStatus } =
                            assignment.assignmentList[0];
                        return (
                            requestStatus !== 'Complete' &&
                            assignmentTask === 'Coordinator'
                        );
                    },
                };
                break;
        }
        this.setAssignmentStatusFilters(detail!);
        return detail;
    }

    private setState(
        assignments: AssignmentBillResult[],
        state: AssignmentState,
        isLoading = false,
    ) {
        state.assignments = assignments;
        state.isLoading = isLoading;
    }

    private setAssignmentStatusFilters(detail: LoadDetail) {
        const parentFilters = detail.report.request.filters;
        if (parentFilters[1].filters) {
            parentFilters[1].filters[0].values = detail.requestStatus;
        }
        if (parentFilters[2].filters) {
            //parentFilters[2].filters[0].values = detail.assignmentStatus; // commenting so as to not filter out Pending assignments
            parentFilters[2].filters[1].values = detail.assignmentTask;
        }
    }

    private getAssignmentsForType(detail: LoadDetail) {
        return this.reports
            .loadAssignment(
                this.user.id,
                detail.assignmentStatus[0],
                detail.report,
            )
            .pipe(catchError((error) => this.handleError(error)));
    }

    private initUserAssignmentReport(session: string): AssignmentReport {
        return {
            id: 0,
            name: 'Active Analyses',
            type: 'Assignment',
            request: {
                type: 'Assignment',
                take: 100,
                skip: 0,
                page: 1,
                filters: [
                    {
                        name: 'Session',
                        type: 'Bill',
                        path: 'Bill.Session',
                        comparison: 'String',
                        values: [session],
                        exclude: false,
                        active: true,
                    },
                    {
                        name: 'Analysis',
                        type: 'Analysis',
                        path: 'Instance.Requests',
                        comparison: 'Analysis',
                        instances: [],
                        values: ['Any'],
                        exclude: false,
                        active: true,
                        filters: [
                            {
                                name: 'Status',
                                type: 'Analysis',
                                path: 'Request.Status',
                                comparison: 'Or',
                                values: [],
                                exclude: false,
                                active: true,
                            },
                            {
                                name: 'Priority',
                                type: 'Analysis',
                                path: 'Request.Priority',
                                comparison: 'Or',
                                values: [],
                                exclude: false,
                                active: true,
                            },
                            {
                                name: 'Due',
                                type: 'Analysis',
                                path: 'Request.Due',
                                comparison: 'Date',
                                values: [],
                                exclude: false,
                                active: true,
                            },
                        ],
                    },
                    {
                        name: 'Assignment',
                        type: 'Assignment',
                        path: 'Assignment.User',
                        comparison: 'Assignment',
                        values: [{ ID: this.user.id }],
                        exclude: false,
                        active: true,
                        filters: [
                            {
                                name: 'Status',
                                type: 'Assignment',
                                path: 'Assignment.Status',
                                comparison: 'Or',
                                values: [],
                                exclude: false,
                                active: true,
                            },
                            {
                                name: 'Task',
                                type: 'Assignment',
                                path: 'Assignment.Task',
                                comparison: 'Or',
                                values: [],
                                exclude: false,
                                active: true,
                            },
                        ],
                    },
                ],
                display: [
                    {
                        name: 'Assignment',
                        type: 'Assignment',
                        display: 'Assignment',
                        path: 'Instance.Requests.Assignments',
                    },
                ],
                sort: [
                    {
                        name: 'Measure',
                        direction: 'Ascending',
                    },
                ],
            },
            response: {
                total: 0,
                results: [],
            },
        };
    }

    private groupRequestsTogether(
        assignments: AssignmentBillResult[],
    ): AssignmentBillResult[] {
        const map = assignments.reduce((acc, assignment) => {
            const billId = assignment.billID;
            acc.has(billId)
                ? acc
                      .get(billId)!
                      .assignmentList.push(...assignment.assignmentList)
                : acc.set(billId, _.cloneDeep(assignment));
            return acc;
        }, new Map<string, AssignmentBillResult>());
        return Array.of(...map.values());
    }

    private handleError(error: ApiError) {
        console.error(error.message);
        return EMPTY;
    }
}

/**
 * A convenience type to hold the assignments and its loading state.
 */
type AssignmentState = {
    isLoading: boolean;
    assignments: AssignmentBillResult[];
};
